<script setup lang="ts">
import BJointRadioItem from '@/components/BJointRadioItem.vue';
import BSelect from '@/components/BSelect.vue';
import Info from '@/components/icons/Info.vue';
import { useStore } from '@/stores/main';
import { ProfileValueChange } from '@/types';
import { useDebounceFn } from '@vueuse/core';
import { computed, inject, ref, watch, onMounted, onUnmounted } from 'vue';

const categoryId = 4;

const emit = defineEmits<{
  (e: 'change', value: ProfileValueChange): void;
  (e: 'update'): void;
}>();

const store = useStore();

const factor = computed(() => store.profile.factors?.[categoryId]);

// Show improve section when we have a valid calculated value (risk_value is set and not null/undefined)
// OR when the factor status is 'risk' or 'healthy' (not 'unknown')
const showImprove = computed(() => {
  const f = factor.value;
  if (!f) return false;
  // Check if we have a valid risk_value
  if (f.risk_value != null) return true;
  // Also check if status indicates a calculated result
  if (f.status && f.status !== 'unknown' && f.status !== 'profile.unknown') return true;
  // Check if value is set (fallback)
  if (f.value != null) return true;
  return false;
});

const fitMethod = computed(() => {
  let value = store.profile.values?.fitMethod;

  if (value === undefined) {
    value = null;
  }

  return value;
});

async function onChange(e: { name: string; value: string }) {
  const result = await store.setProfileValue(e.name, e.value);

  emit('update');
}

async function handleImprove(value: string) {
  await store.setImproveValue(categoryId, value);
}

const onInputChange = useDebounceFn((name: string, event: InputEvent) => {
  const value = (event.target as HTMLInputElement)?.value ?? '';
  onChange({ name, value });
}, 750);

const onSelectChange = useDebounceFn((name: string, value: any) => {
  onChange({ name, value: value ?? '' });
}, 300);

const parentScroll = inject('shouldScroll') as any;
watch(showImprove, () => {
  if (showImprove.value) {
    setTimeout(() => {
      parentScroll.scrollDown();
    }, 100);
  }
});

const colorScheme = inject('colorScheme', 'green');
const borderColor = computed(() => {
  return (
    {
      purple: 'border-purple-light',
      yellow: 'border-yellow-light',
      green: 'border-green-light',
      blue: 'border-blue-light',
    }[colorScheme] ?? 'border-green-light'
  );
});

const improve = computed(() => {
  return factor.value?.improve || null;
});

// Rating scale value - derived directly from store factor, no localStorage
const currentValue = computed(() => {
  const f = factor.value;
  if (!f) return '';
  // Prefer risk_value (calculated result), fallback to value
  if (f.risk_value !== undefined && f.risk_value !== null) {
    return String(f.risk_value);
  }
  if (f.value !== undefined && f.value !== null) {
    return String(f.value);
  }
  return '';
});

const bgColors = [
  'bg-white',
  'bg-red-400',
  'bg-orange-400',
  'bg-amber-300',
  'bg-lime-300',
  'bg-green-400',
];

const highlightSwatch = (index: number) => {
  // First check if we have a risk_value from the factor
  if (factor.value?.risk_value !== undefined && String(factor.value.risk_value) === String(index)) {
    return 'scale-150';
  }
  
  // Then check currentValue from localStorage if available
  if (currentValue.value === String(index)) {
    return 'scale-150';
  }
  
  return '';
};

// Detect steptest with low Borg value (< 12) which produces "okänt värde"
const isSteptestLowBorg = computed(() => {
  if (fitMethod.value === undefined || fitMethod.value === null) return false;
  if (+fitMethod.value !== 0) return false;
  const borg = store.profile.values?.['fitBorgSteady'];
  if (borg === undefined || borg === null || borg === '') return false;
  return +borg < 12;
});

// Toggle for showing debug info
const showDebug = ref(false);
</script>

<template>
  <div class="px-8 pt-6 gap-y-4 w-full lg:w-[60%] pb-12">
    <div class="flex items-center gap-x-4 mt-2 mb-1">
      <h3 class="m-0 font-light">Kondition</h3>
    </div>

    <p class="text-base mb-1 lg:text-start">Maxtest</p>

    <ul
      class="p-0 m-0 bg-white rounded border-green-light border-solid border divide-y divide-green-light divide-solid divide-x-0"
    >
      <b-joint-radio-item
        color="green"
        name="fitMethod"
        :data="{ label: 'Coopertest', value: 7 }"
        @picked="onChange({ name: 'fitMethod', value: '7' })"
        :vertical="false"
      />
      <b-joint-radio-item
        color="green"
        name="fitMethod"
        :data="{ label: 'Beeptest', value: 6 }"
        @picked="onChange({ name: 'fitMethod', value: '6' })"
        :vertical="false"
      />
    </ul>

    <p class="text-base mb-1 lg:text-start">Submaxtest</p>

    <ul
      class="p-0 m-0 bg-white rounded border-green-light border-solid border divide-y divide-green-light divide-solid divide-x-0"
    >
      <b-joint-radio-item
        color="green"
        name="fitMethod"
        :data="{ label: 'Steptest', value: 0 }"
        @picked="onChange({ name: 'fitMethod', value: '0' })"
        :vertical="false"
      />
      <b-joint-radio-item
        color="green"
        name="fitMethod"
        :data="{ label: 'Ergometercykel', value: 1 }"
        @picked="onChange({ name: 'fitMethod', value: '1' })"
        :vertical="false"
      />
      <b-joint-radio-item
        color="green"
        name="fitMethod"
        :data="{ label: '1-Mile walk test', value: 5 }"
        @picked="onChange({ name: 'fitMethod', value: '5' })"
        :vertical="false"
      />
    </ul>

    <div v-if="fitMethod !== undefined && fitMethod !== null" class="mt-8 pb-8">
      <template v-if="+fitMethod === 7">
        <h3 class="font-light mt-0">Coopertest</h3>

        <p class="text-base">Fyll i antal meter du sprungit på 12 minuter</p>

        <div class="flex text-sm gap-x-1 mb-4 items-start">
          <Info class="text-green-darker shrink-0" width="20" />
          Förslag – ladda ner Runkeeper på din mobil
        </div>

        <div class="flex mb-6 items-center">
          <b-input
            @input="onInputChange('fitCooperDistance', $event)"
            v-model="store.profile.values['fitCooperDistance']"
            :haveButton="false"
          />
          <span class="ml-2 w-8">m</span>
        </div>
      </template>

      <template v-else-if="+fitMethod === 6">
        <h3 class="font-light mt-0">Beeptest</h3>

        <div class="space-y-2">
          <div class="flex items-center space-x-4">
            <span class="block w-24 flex-none">Level</span>
            <b-input
              @input="onInputChange('fitBeepLevel', $event)"
              v-model="store.profile.values['fitBeepLevel']"
              class="max-w-24"
              :haveButton="false"
            />
          </div>
          <div class="flex items-center space-x-4">
            <span class="block w-24 flex-none">Number of shuttles</span>
            <b-input
              @input="onInputChange('fitBeepShuttles', $event)"
              v-model="store.profile.values['fitBeepShuttles']"
                class="max-w-24"
                :haveButton="false"
            />
          </div>
        </div>
      </template>

      <template v-else-if="+fitMethod === 0">
        <h3 class="font-light mt-0">Steptest</h3>

        <p class="text-base">Steady state</p>

        <div class="grid gap-x-4 gap-y-4">
  
        
          <div class="flex items-center gap-6">
            <span>Puls</span>
            <b-input
              @input="onInputChange('fitPulseSteady', $event)"
              v-model="store.profile.values['fitPulseSteady']"
              class="max-w-24"
              :haveButton="false"
            />
          </div>

          <div class="flex items-center gap-4">
            <span>(Borg)</span>
            <b-select
              @change="onSelectChange('fitBorgSteady', $event)"
              v-model="store.profile.values['fitBorgSteady']"
            >
              <option :value="null">&nbsp;</option>
              <option v-for="(v, i) in Array(15).keys()" :key="i" :value="v + 6">
                {{ v + 6 }}
              </option>
            </b-select>
          </div>

          <div class="flex items-center gap-4">
            
          <span>Bänkhöjd</span>
            <b-select
              @change="onSelectChange('fitBenchSteady', $event)"
              v-model="store.profile.values['fitBenchSteady']"
            >
              <option :value="null">&nbsp;</option>
              <option :value="33">33</option>
              <option :value="40">40</option>
            </b-select>
          </div>
        </div>
      </template>

      <template v-else-if="+fitMethod === 1">
        <h3 class="font-light mt-0">Ergometercykel</h3>

        <p class="text-base">Steady state</p>

        <div class="grid gap-x-4 gap-y-2">
          
          

          <div class="flex items-center gap-6">
          <span>Puls</span>
            
            <b-input
              @input="onInputChange('fitBicPulseSteady', $event)"
              v-model="store.profile.values['fitBicPulseSteady']"
              class="max-w-24"
              :haveButton="false"
            />
          </div>

          <div class="flex items-center gap-4">
            <span>(Borg)</span>
            <b-select
              @change="onSelectChange('fitBicBorgSteady', $event)"
              v-model="store.profile.values['fitBicBorgSteady']"
            >
              <option :value="null">&nbsp;</option>
              <option v-for="(v, i) in Array(15).keys()" :key="i" :value="v + 6">
                {{ v + 6 }}
              </option>
            </b-select>
          </div>

          <div class="flex items-center gap-7">
            <span>Watt</span>
            <b-select
              @change="onSelectChange('fitBicWattsSteady', $event)"
              v-model="store.profile.values['fitBicWattsSteady']"
            >
              <option :value="null">&nbsp;</option>
              <option
                v-for="(v, i) in Array(10).keys()"
                :key="i"
                :value="(v + 2) * 25"
              >
                {{ (v + 2) * 25 }}
              </option>
            </b-select>
          </div>
        </div>
      </template>

      <template v-else-if="+fitMethod === 5">
        <h3 class="font-light mt-0">1-Mile walk test</h3>

        <ul class="text-base ml-0 pl-4 space-y-2">
          <li>
            Gå eller spring 12 minuter så snabbt du kan.<br />
            <span class="opacity-60"
              >Ladda ner appen runkeeper som mäter sträckan du avverkat.</span
            >
          </li>
          <li>
            Ta pulsen: Lägg 3 fingrar mot halspulsådern, räkna antal slag under
            15 sek och multiplicera sedan med 4 för att få antal slag per minut.
          </li>
        </ul>

        <div class="space-y-2">
          <div class="flex items-center space-x-4">
            <span class="block w-24 flex-none">Puls</span>
            <b-input
              @input="onInputChange('fitWalkPulse', $event)"
              v-model="store.profile.values['fitWalkPulse']"
              class="max-w-24"
              :haveButton="false"
            />
          </div>
          <div class="flex items-center space-x-4">
            <span class="block w-24 flex-none">Sträcka</span>
            <b-input
              @input="onInputChange('fitWalkDistance', $event)"
              v-model="store.profile.values['fitWalkDistance']"
               class="max-w-24"
               :haveButton="false"
            />
            <!-- <span>m</span> -->
          </div>
          <div class="flex items-center space-x-4">
            <span class="block w-24 flex-none">Minuter</span>
            <b-input
              @input="onInputChange('fitWalkMin', $event)"
              v-model="store.profile.values['fitWalkMin']"
               class="max-w-24"
               :haveButton="false"
            />
          </div>
          <div class="flex items-center space-x-4">
            <span class="block w-24 flex-none">Sekunder</span>
            <b-input
              @input="onInputChange('fitWalkSec', $event)"
              v-model="store.profile.values['fitWalkSec']"
               class="max-w-24"
               :haveButton="false"
            />
          </div>
        </div>
      </template>
    </div>

    <div
      v-if="showImprove"
      :class="borderColor"
      class="border-t border-0 border-solid mt-8 py-4 text-base"
    >
      <div v-if="isSteptestLowBorg" class="text-amber-700 bg-amber-50 border border-amber-300 rounded p-3">
        Borgskalan visar att du har hög maxpuls. Helt normalt men för att få rätt resultat måste du göra maxtest.
      </div>
      <div v-else>Du har {{ factor?.label === 'okänd' ? 'okänt' : (factor?.label || '') }} värde.</div>

      <!-- Fitness Rating Scale -->
      <div class="w-full flex justify-center my-6">
        <div
          class="flex justify-center space-x-2 items-center text-base w-full max-w-md"
        >
          <div class="flex-1 flex text-sm items-center">
            <div
              class="flex-1 border-0 border-t border-solid border-black/30"
            ></div>
            <div class="px-1">Risk</div>
          </div>
          <div class="flex flex-col items-center">
            <div class="flex space-x-2">
              <span
                :class="[
                  'flex items-center justify-center w-5 h-5 outline outline-1 rounded-full text-sm transition',
                  bgColors[1],
                  highlightSwatch(1),
                ]"
                >1</span
              >
              <span
                :class="[
                  'flex items-center justify-center w-5 h-5 outline outline-1 rounded-full text-sm transition',
                  bgColors[2],
                  highlightSwatch(2),
                ]"
                >2</span
              >
              <span
                :class="[
                  'flex items-center justify-center w-5 h-5 outline outline-1 rounded-full text-sm transition',
                  bgColors[3],
                  highlightSwatch(3),
                ]"
                >3</span
              >
              <span
                :class="[
                  'flex items-center justify-center w-5 h-5 outline outline-1 rounded-full text-sm transition',
                  bgColors[4],
                  highlightSwatch(4),
                ]"
                >4</span
              >
              <span
                :class="[
                  'flex items-center justify-center w-5 h-5 outline outline-1 rounded-full text-sm transition',
                  bgColors[5],
                  highlightSwatch(5),
                ]"
                >5</span
              >
            </div>
          </div>
          <div class="flex-1 flex text-sm items-center">
            <div class="px-1">Frisk</div>
            <div
              class="flex-1 border-0 border-t border-solid border-black/30"
            ></div>
          </div>
        </div>
      </div>

      <ul class="flex flex-col m-0 mt-4 p-0">
        <li class="list-none">
          <b-radio
            :name="`fitness-improve`"
            :color="colorScheme"
            value="1"
            label="Detta är jag nöjd med"
            :label-id="`fitness-improve-1-label`"
            @change="handleImprove('1')"
            v-model="improve"
          />
        </li>

        <li class="list-none">
          <b-radio
            :name="`fitness-improve`"
            :color="colorScheme"
            value="2"
            label="Detta vill jag förbättra"
            :label-id="`fitness-improve-2-label`"
            @change="handleImprove('2')"
            v-model="improve"
          />
        </li>
      </ul>
      
      <!-- Debug button -->
      <div class="flex justify-center mt-4 mb-2 hidden">
        <button 
          @click="showDebug = !showDebug" 
          class="text-xs text-gray-500 underline"
        >
          {{ showDebug ? 'Dölj debug info' : 'Visa debug info' }}
        </button>
      </div>
      
      <!-- Debug Information -->
      <div v-if="showDebug" class="debug-info hidden mt-2 p-4 border border-gray-300 rounded bg-gray-100 text-sm">
        <h4 class="font-bold mb-2">Debug Information</h4>
        <div class="mb-2">
          <strong>Category ID:</strong> {{ categoryId }}
        </div>
        <div class="mb-2">
          <strong>Current Value:</strong> {{ currentValue }}
        </div>
        <div class="mb-2">
          <strong>Factor Value:</strong> {{ factor?.value }}
        </div>
        <div class="mb-2">
          <strong>Risk Value:</strong> {{ factor?.risk_value }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Transitions for smoother scale effect */
.scale-150 {
  transform: scale(1.5);
  transition: transform 0.3s ease;
}

span {
  transition: transform 0.3s ease, background-color 0.3s ease;
}

/* Debug panel */
.debug-info {
  overflow: auto;
  max-height: 300px;
  font-family: monospace;
  line-height: 1.4;
}
</style>